// Production-Ready FAQ Viewer
// Features: Search, category filtering, accordion Q&A, export (PDF/DOCX)

import React, { useState, useMemo } from 'react';
import {
  Search,
  ChevronDown,
  ChevronRight,
  HelpCircle,
  Download,
  FileText,
  FileImage,
  MessageCircleQuestion,
  Filter,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { FAQResponse, FAQItem } from '@/lib/content-studio/types';
import { useContentExporter } from '@/hooks/useContentExporter';

interface FAQViewerProps {
  faq: FAQResponse;
  className?: string;
}

export function FAQViewer({ faq, className = '' }: FAQViewerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const { exportToDocx, exportToPdf, isExporting } = useContentExporter();

  // Filter items based on search and category
  const filteredItems = useMemo(() => {
    let items = faq.items;

    // Filter by category
    if (activeCategory !== 'All') {
      items = items.filter((item) => item.category === activeCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      items = items.filter(
        (item) =>
          item.question.toLowerCase().includes(query) ||
          item.answer.toLowerCase().includes(query)
      );
    }

    return items;
  }, [faq.items, searchQuery, activeCategory]);

  // Category options including "All"
  const categoryOptions = useMemo(() => {
    return ['All', ...faq.categories];
  }, [faq.categories]);

  // Count items per category
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { All: faq.items.length };
    faq.items.forEach((item) => {
      const cat = item.category || 'Uncategorised';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  }, [faq.items]);

  // Toggle expand/collapse for an item
  const toggleItem = (itemId: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  // Expand all / Collapse all
  const expandAll = () => {
    setExpandedItems(new Set(filteredItems.map((item) => item.id)));
  };

  const collapseAll = () => {
    setExpandedItems(new Set());
  };

  // Export handlers
  const handleExportPdf = () => {
    exportToPdf('faq', faq);
  };

  const handleExportDocx = () => {
    exportToDocx('faq', faq);
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <MessageCircleQuestion className="h-5 w-5 text-primary shrink-0" />
            <h2 className="text-xl font-bold tracking-tight truncate">
              {faq.title}
            </h2>
          </div>
          {faq.description && (
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
              {faq.description}
            </p>
          )}
        </div>

        {/* Export dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={isExporting}
              className="shrink-0"
            >
              <Download className="h-4 w-4 mr-1.5" />
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleExportPdf}>
              <FileImage className="h-4 w-4 mr-2" />
              Export as PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleExportDocx}>
              <FileText className="h-4 w-4 mr-2" />
              Export as Word (.docx)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search questions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Category filter pills */}
      {categoryOptions.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          {categoryOptions.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors',
                activeCategory === category
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
              )}
            >
              {category}
              <span
                className={cn(
                  'text-[10px] rounded-full px-1.5 py-0.5 min-w-[18px] text-center',
                  activeCategory === category
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-background/60 text-muted-foreground'
                )}
              >
                {categoryCounts[category] || 0}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Results count + expand/collapse controls */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing{' '}
          <span className="font-medium text-foreground">
            {filteredItems.length}
          </span>{' '}
          of{' '}
          <span className="font-medium text-foreground">
            {faq.items.length}
          </span>{' '}
          questions
        </p>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={expandAll}
            className="text-xs h-7 px-2"
          >
            Expand all
          </Button>
          <Separator orientation="vertical" className="h-4" />
          <Button
            variant="ghost"
            size="sm"
            onClick={collapseAll}
            className="text-xs h-7 px-2"
          >
            Collapse all
          </Button>
        </div>
      </div>

      {/* FAQ items */}
      <ScrollArea className="max-h-[600px]">
        {filteredItems.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Search className="h-10 w-10 text-muted-foreground/40 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">
                No questions match your search
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Try adjusting your search terms or category filter
              </p>
              {(searchQuery || activeCategory !== 'All') && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-3 text-xs"
                  onClick={() => {
                    setSearchQuery('');
                    setActiveCategory('All');
                  }}
                >
                  Clear filters
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filteredItems.map((item, index) => (
              <FAQAccordionItem
                key={item.id}
                item={item}
                index={index}
                isExpanded={expandedItems.has(item.id)}
                onToggle={() => toggleItem(item.id)}
                searchQuery={searchQuery}
              />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

// Highlight matching text in search results
function HighlightText({
  text,
  query,
}: {
  text: string;
  query: string;
}) {
  if (!query.trim()) {
    return <>{text}</>;
  }

  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark
            key={i}
            className="bg-yellow-200 dark:bg-yellow-800/50 text-inherit rounded-sm px-0.5"
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

// Individual FAQ accordion item
function FAQAccordionItem({
  item,
  index,
  isExpanded,
  onToggle,
  searchQuery,
}: {
  item: FAQItem;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  searchQuery: string;
}) {
  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <Card
        className={cn(
          'transition-all duration-200',
          isExpanded
            ? 'ring-1 ring-primary/20 shadow-sm'
            : 'hover:bg-muted/30'
        )}
      >
        <CollapsibleTrigger asChild>
          <button className="w-full text-left px-4 py-3.5 flex items-start gap-3 group">
            <div className="shrink-0 mt-0.5">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-primary transition-transform" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <span
                className={cn(
                  'text-sm font-medium leading-relaxed',
                  isExpanded ? 'text-primary' : 'text-foreground'
                )}
              >
                <HighlightText text={item.question} query={searchQuery} />
              </span>
            </div>
            {item.category && (
              <Badge
                variant="secondary"
                className="shrink-0 text-[10px] font-normal ml-2"
              >
                {item.category}
              </Badge>
            )}
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 pt-0">
            <Separator className="mb-3" />
            <div className="pl-7">
              <div className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                <HighlightText text={item.answer} query={searchQuery} />
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

export default FAQViewer;
